import { FC } from 'react';
import classNames from 'classnames';

import { getLogger } from '@rikstv/play-common/src/utils/logger/logger';
import { RequiredProps } from '@rikstv/play-common/src/utils/types/typeUtils';
import { H3, Tag } from '@rikstv/shared-components';

import { SwimlaneItem } from '../../../forces/assetDetails/assetDetails.types';
import { DurationBar } from '../../durationBar/DurationBar';
import { ImageFade } from '../../imageFade/ImageFade';
import { ClampLines } from '../../truncateText/ClampLines';
import { FavoriteChannelButton } from '../favoriteChannelButton/favoriteChannelButton';
import { getLinkTo } from '../swimlane.utils';
import { buildLiveChannelItemAriaLabel } from '../swimlaneItemAriaLabel';

import { ItemWrapper } from './ItemWrapper';

import styles from './LiveChannelItem.module.css';

const logger = getLogger('[Swimlane]');

const ProgramTimeInfo: FC<{ asset: SwimlaneItem }> = ({ asset }) => {
  if (asset.programState.type !== 'Live') {
    return null;
  }
  return (
    <>
      <Tag className={styles.time}>{asset.subtitle}</Tag>
      <DurationBar percent={asset.programState.progressPercentage ?? 0} className={styles.durationBar} />
    </>
  );
};

interface LiveChannelItemProps {
  asset: RequiredProps<SwimlaneItem, 'channel'>;
  onClick?: () => void;
  preventNavigation?: boolean;
}

export const LiveChannelItem: FC<LiveChannelItemProps> = ({ asset, onClick, preventNavigation }) => {
  if (asset.channel == null) {
    // Extra check to prevent crashes since we've seen this happen
    // when backup/restore logic in OVP fails.
    logger.warn('LiveChanelItem missing channel info', asset);
    return null;
  }

  return (
    <div className={styles.itemWrapper}>
      <ItemWrapper
        className={classNames(styles.liveChannelItem, 'with-keyboard-focus')}
        linkTo={getLinkTo(asset)}
        ariaLabel={buildLiveChannelItemAriaLabel(asset)}
        preventDefault={preventNavigation}
        onClick={onClick}>
        <ImageFade src={asset.channel.logo} alt={asset.channel.name} className={styles.logo} />
        <ProgramTimeInfo asset={asset} />
        <ClampLines textStyle={H3} lookLike="title-4" className={styles.programName}>
          {asset.title}
        </ClampLines>
      </ItemWrapper>
      {asset.links.myList?.href && (
        <FavoriteChannelButton
          isDefaultFavorite={asset.userData?.isInMyList ?? false}
          url={asset.links.myList.href}
          channelName={asset.channel.name}
          channelId={asset.channel.id}
          className={styles.favoriteButton}
          key={`live-channel-favorite-button-${asset.id}-${asset.userData?.isInMyList}`}
        />
      )}
    </div>
  );
};
